import { addWeeks, endOfWeek, startOfWeek } from 'date-fns';
import { fromZonedTime, toZonedTime } from 'date-fns-tz';

import { COLLECTION_TIME_FILTERS as TIME_FILTERS } from 'constants/collections.constants';
import { SiteLocale } from 'constants/i18n.constants';
import { datoRequester } from 'queries/requesters';
import { InputMaybe, PageEventModelFilter } from 'types/api/dato.types';
import {
  CollectionFragment,
  PageEventsDocument,
} from 'types/api/dato-operations.types';

type TimeFilterValue = (typeof TIME_FILTERS)[keyof typeof TIME_FILTERS];
const Sunday = 0;
const weekStartsOn = Sunday;

/**
 * Get week start (dd/mm/yyyy 00:00:00) and end (dd/mm/yyyy 23:59:59) in UTC timezone
 */
const getWeekBoundariesUTC = (date: Date) => {
  const utcDate = toZonedTime(date, 'UTC');
  const start = startOfWeek(utcDate, { weekStartsOn });
  const end = endOfWeek(utcDate, { weekStartsOn });
  const startUTC = fromZonedTime(start, 'UTC');
  const endUTC = fromZonedTime(end, 'UTC');
  return { start: startUTC, end: endUTC };
};

/**
 * Get date range for time filter
 * Note: we are using UTC timezone
 * because this is default timezone in DatoCMS config
 */
const getDateRangeForTimeFilter = (timeFilter: TimeFilterValue) => {
  const referenceDate = new Date();

  switch (timeFilter) {
    case TIME_FILTERS.THIS_WEEK: {
      return getWeekBoundariesUTC(referenceDate);
    }

    case TIME_FILTERS.NEXT_WEEK: {
      const nextWeek = addWeeks(referenceDate, 1);
      return getWeekBoundariesUTC(nextWeek);
    }

    default:
      return null;
  }
};

/**
 * Build GraphQL filter for collection events based on collection settings
 */
export const buildCollectionEventFilter = (
  baseFilter?: InputMaybe<PageEventModelFilter>,
  collectionFilters?: {
    timeFilter?: string | null;
    venue?: { id: string } | null;
  },
): InputMaybe<PageEventModelFilter> => {
  const eventFilter: InputMaybe<PageEventModelFilter> = baseFilter ?? {};

  // Add venue filter if specified in collection
  if (collectionFilters?.venue?.id) {
    eventFilter.venue = { eq: collectionFilters.venue.id };
  }

  // Add time filter if specified in collection
  if (collectionFilters?.timeFilter) {
    const dateRange = getDateRangeForTimeFilter(
      collectionFilters.timeFilter as TimeFilterValue,
    );
    if (dateRange) {
      eventFilter.startDatetime = {
        gte: dateRange.start.toISOString(),
        lte: dateRange.end.toISOString(),
      };
    }
  }

  return eventFilter;
};

/**
 * Page home show collection cards with events count
 * Now that we have filters on the collections (time and venue),
 * we need to apply same filters to page home collections
 */
export const fetchFilteredCollectionsForHome = async (
  collections: Array<CollectionFragment>,
  currentISOString: string,
  locale?: SiteLocale,
) => {
  const collectionQueries = collections.map(async (collection) => {
    const eventFilter = buildCollectionEventFilter(
      {
        collections: { allIn: [collection.id] },
        endDatetime: { gte: currentISOString },
        unlistedEvent: { eq: false },
      },
      {
        timeFilter: collection.timeFilter,
        venue: collection.venue,
      },
    );

    try {
      const result = await datoRequester(PageEventsDocument, {
        locale: locale ?? SiteLocale.EN,
        filter: eventFilter,
        first: 1,
        skip: 0,
      })(undefined);

      return {
        ...collection,
        references: { count: result.meta.count },
      };
    } catch (error) {
      return collection;
    }
  });

  const results = await Promise.all(collectionQueries);

  return results.filter(({ references }) => references.count > 0);
};
